import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Box, FormControl, InputLabel, Select, MenuItem, Typography } from '@mui/material';

// Explicitly unregister datalabels plugin if it exists to prevent unwanted labels
if (ChartJS.registry?.plugins?.get('datalabels')) {
  ChartJS.unregister('datalabels');
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

/**
 * Journey Timeline Chart
 * 📈 Interactive timeline visualization for journey goals
 */
const JourneyTimelineChart = ({ 
  timelineData = [], 
  goalName = 'Goal',
  currentAmount = 0,
  targetAmount = 0,
  monthlySavings = 0,
  interestRate = 0.04,
  timePeriod = 'annual',
  onTimePeriodChange,
  timeToGoalMonths = null // Optional: if provided, use this to auto-adjust timeline
}) => {
  // Calculate projected timeline based on current metrics
  // This uses proper compound interest formula to show exponential growth
  const projectedData = useMemo(() => {
    if (!monthlySavings || monthlySavings <= 0) return [];
    
    // interestRate is a percentage (e.g., 10.1 for 10.1%), convert to decimal
    const annualRate = interestRate / 100;
    const monthlyRate = annualRate / 12;
    
    // Calculate exact time to goal - must match Projection Summary calculation
    // If timeToGoalMonths is provided, use it exactly (no buffer)
    // Otherwise, calculate it the same way as Projection Summary
    let exactGoalMonths = 0;
    if (timeToGoalMonths && timeToGoalMonths > 0) {
      exactGoalMonths = timeToGoalMonths;
    } else {
      // Calculate exactly like Projection Summary does
      const monthlyRate = annualRate / 12;
      let months = 0;
      let amount = currentAmount;
      const maxMonthsCalc = 1200; // 100 years max
      
      while (amount < targetAmount && months < maxMonthsCalc) {
        amount = amount * (1 + monthlyRate) + monthlySavings;
        months++;
        if (amount >= targetAmount) break;
      }
      exactGoalMonths = months >= maxMonthsCalc ? maxMonthsCalc : months;
    }
    
    // Use exact goal months - NO buffer, stop exactly at goal
    // The chart should end exactly when the goal is reached
    const maxMonths = exactGoalMonths > 0 ? exactGoalMonths : 1200;
    
    // Determine step size and max points based on time period
    // For annual/quarterly views, we calculate monthly data points for smooth exponential curve
    // Chart.js will use all points to draw the curve, but we'll only label at intervals
    let stepMonths = 1; // Default: monthly
    let maxPoints = maxMonths;
    
    switch (timePeriod) {
      case 'daily':
        stepMonths = 1/30; // ~Daily
        maxPoints = Math.min(maxMonths * 30, 1000); // Cap points for performance
        break;
      case 'weekly':
        stepMonths = 1/4.33; // ~Weekly
        maxPoints = Math.min(maxMonths * 4.33, 500);
        break;
      case 'monthly':
        stepMonths = 1; // Monthly
        maxPoints = maxMonths;
        break;
      case 'quarterly':
        stepMonths = 1; // Calculate monthly for smooth curve
        maxPoints = maxMonths; // Use all monthly points
        break;
      case 'annual':
        stepMonths = 1; // Calculate monthly for smooth exponential curve
        maxPoints = maxMonths; // Use all monthly points (e.g., 264 points for 22 years)
        break;
    }
    
    // Calculate using direct exponential compound interest formula
    // Formula: FV = PV * (1 + r)^n + PMT * [((1 + r)^n - 1) / r]
    // Where: PV = present value, r = monthly rate, n = number of months, PMT = monthly payment
    // This formula directly calculates exponential growth without linear interpolation
    
    const data = [];
    
    // Pre-calculate monthly data for accurate tracking
    const monthlyData = [];
    let balance = currentAmount;
    let totalContributions = 0;
    let totalInterest = 0;
    
    // Calculate all monthly points first - must match Projection Summary exactly
    let goalReachedMonth = null;
    for (let month = 0; month <= maxMonths; month++) {
      if (month === 0) {
        monthlyData.push({
          month: 0,
          balance: currentAmount,
          totalContributions: 0,
          totalInterest: 0,
          total: currentAmount
        });
      } else {
        // Calculate interest on current balance BEFORE adding savings (matches Projection Summary)
        const interestEarned = balance * monthlyRate;
        
        // Apply compound interest: exponential growth formula (matches Projection Summary)
        balance = balance * (1 + monthlyRate) + monthlySavings;
        
        // Track cumulative totals
        totalContributions += monthlySavings;
        totalInterest += interestEarned;
        
        // Check if we've reached or exceeded target
        if (balance >= targetAmount) {
          // Cap at target amount exactly
          balance = targetAmount;
          goalReachedMonth = month;
          monthlyData.push({
            month,
            balance: targetAmount,
            totalContributions: totalContributions,
            totalInterest: totalInterest,
            total: targetAmount
          });
          // Stop here - don't continue beyond goal
          break;
        }
        
        monthlyData.push({
          month,
          balance: balance,
          totalContributions: totalContributions,
          totalInterest: totalInterest,
          total: balance
        });
      }
    }
    
    // Validation: Log final values to verify they match Projection Summary
    if (monthlyData.length > 0) {
      const finalData = monthlyData[monthlyData.length - 1];
      const expectedTotal = currentAmount + finalData.totalContributions + finalData.totalInterest;
      console.log('📊 [JourneyTimelineChart] Monthly calculation results:', {
        totalMonths: monthlyData.length,
        finalMonth: finalData.month,
        finalTotal: finalData.total,
        finalContributions: finalData.totalContributions,
        finalInterest: finalData.totalInterest,
        targetAmount,
        currentAmount,
        expectedTotal,
        goalReached: goalReachedMonth !== null,
        goalReachedMonth,
        exactGoalMonths,
        maxMonths,
        matchesTarget: Math.abs(finalData.total - targetAmount) < 0.01,
        calculationMatches: Math.abs(expectedTotal - finalData.total) < 0.01,
        monthlyRate,
        annualRate: interestRate
      });
    }
    
    // Now sample based on time period
    // For annual/quarterly views, we use monthly data points for smooth exponential curve
    // but only label at the display intervals
    for (let point = 0; point <= maxPoints; point++) {
      const monthsElapsed = point * stepMonths;
      const wholeMonths = Math.floor(monthsElapsed);
      const partialMonth = monthsElapsed - wholeMonths;
      
      // If no time has passed
      if (monthsElapsed <= 0) {
        data.push({
          period: point,
          month: 0,
          amount: currentAmount,
          contributions: 0,
          interest: 0,
          total: currentAmount,
          target: targetAmount
        });
        continue;
      }
      
      // Get the base monthly data point
      const baseIndex = Math.min(wholeMonths, monthlyData.length - 1);
      const baseData = monthlyData[baseIndex];
      
      if (!baseData) continue;
      
      // For partial months, calculate exponential growth for the partial period
      let finalBalance = baseData.balance;
      let finalContributions = baseData.totalContributions;
      let finalInterest = baseData.totalInterest;
      
      if (partialMonth > 0 && baseIndex < monthlyData.length - 1) {
        // Calculate exponential growth for the partial month
        const partialRate = monthlyRate * partialMonth;
        const partialSavings = monthlySavings * partialMonth;
        
        // Apply exponential growth for partial month
        finalBalance = baseData.balance * (1 + partialRate) + partialSavings;
        finalContributions = baseData.totalContributions + partialSavings;
        finalInterest = baseData.totalInterest + (baseData.balance * partialRate);
      }
      
      // Stop if we've passed the goal month
      if (goalReachedMonth !== null && wholeMonths > goalReachedMonth) {
        // Ensure we have the goal point, then stop
        const hasGoalPoint = data.some(d => d.month === goalReachedMonth && d.total === targetAmount);
        if (!hasGoalPoint) {
          const goalData = monthlyData[goalReachedMonth];
          if (goalData) {
            data.push({
              period: point,
              month: goalReachedMonth,
              amount: targetAmount,
              contributions: goalData.totalContributions,
              interest: goalData.totalInterest,
              total: targetAmount,
              target: targetAmount
            });
          }
        }
        break;
      }
      
      // If we're at or past the goal month, use exact goal data
      if (goalReachedMonth !== null && wholeMonths >= goalReachedMonth) {
        const goalData = monthlyData[goalReachedMonth];
        if (goalData) {
          data.push({
            period: point,
            month: goalReachedMonth,
            amount: targetAmount,
            contributions: goalData.totalContributions,
            interest: goalData.totalInterest,
            total: targetAmount,
            target: targetAmount
          });
          break;
        }
      }
      
      // Cap at target for any point that exceeds it
      if (finalBalance >= targetAmount) {
        // Find the exact month when goal was reached
        const goalData = monthlyData.find(d => d.balance >= targetAmount);
        if (goalData) {
          data.push({
            period: point,
            month: goalData.month,
            amount: targetAmount,
            contributions: goalData.totalContributions,
            interest: goalData.totalInterest,
            total: targetAmount,
            target: targetAmount
          });
        } else {
          data.push({
            period: point,
            month: wholeMonths,
            amount: targetAmount,
            contributions: finalContributions,
            interest: finalInterest,
            total: targetAmount,
            target: targetAmount
          });
        }
        break;
      }
      
      // Don't add points beyond the goal month
      if (goalReachedMonth !== null && wholeMonths >= goalReachedMonth) {
        break;
      }
      
      data.push({
        period: point,
        month: wholeMonths,
        amount: finalBalance,
        contributions: finalContributions,
        interest: finalInterest,
        total: finalBalance,
        target: targetAmount
      });
    }
    
    // Ensure the last point is exactly at the target if goal was reached
    if (goalReachedMonth !== null && monthlyData.length > 0) {
      const goalData = monthlyData[goalReachedMonth];
      const lastDataPoint = data[data.length - 1];
      
      // If the last point isn't the goal, add it
      if (goalData && (!lastDataPoint || lastDataPoint.total !== targetAmount || lastDataPoint.month !== goalReachedMonth)) {
        // Remove any points after the goal
        const goalIndex = data.findIndex(d => d.month >= goalReachedMonth);
        if (goalIndex >= 0) {
          data.splice(goalIndex);
        }
        
        // Add the exact goal point
        data.push({
          period: data.length,
          month: goalReachedMonth,
          amount: targetAmount,
          contributions: goalData.totalContributions,
          interest: goalData.totalInterest,
          total: targetAmount,
          target: targetAmount
        });
      }
    }
    
    // Final validation: Log the chart's final data point
    if (data.length > 0) {
      const chartFinalPoint = data[data.length - 1];
      console.log('📈 [JourneyTimelineChart] Chart final data point:', {
        period: chartFinalPoint.period,
        month: chartFinalPoint.month,
        total: chartFinalPoint.total,
        contributions: chartFinalPoint.contributions,
        interest: chartFinalPoint.interest,
        targetAmount,
        matchesTarget: Math.abs(chartFinalPoint.total - targetAmount) < 0.01,
        totalDataPoints: data.length
      });
    }
    
    return data;
  }, [currentAmount, targetAmount, monthlySavings, interestRate, timePeriod, timeToGoalMonths]);

  // Format labels based on time period
  const formatLabel = (period, month) => {
    const date = new Date();
    date.setMonth(date.getMonth() + month);
    
    switch (timePeriod) {
      case 'daily':
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
      case 'weekly':
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
      case 'monthly':
        return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      case 'quarterly':
        const quarter = Math.floor(date.getMonth() / 3) + 1;
        return `Q${quarter} ${date.getFullYear()}`;
      case 'annual':
        return date.getFullYear().toString();
      default:
        return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
    }
  };

  // Calculate milestone points (25%, 50%, 75%, 100%)
  const milestones = useMemo(() => {
    if (projectedData.length === 0) return [];
    const milestones = [];
    const milestonePercentages = [25, 50, 75, 100];
    
    milestonePercentages.forEach(percentage => {
      const targetValue = targetAmount * (percentage / 100);
      const milestonePoint = projectedData.find(d => d.amount >= targetValue);
      if (milestonePoint) {
        milestones.push({
          ...milestonePoint,
          percentage,
          label: `${percentage}%`
        });
      }
    });
    
    return milestones;
  }, [projectedData, targetAmount]);

  const chartData = {
    labels: projectedData.map(d => formatLabel(d.period, d.month)),
    datasets: [
      // Total Contributions line (cumulative)
      {
        label: 'Total Contributions',
        data: projectedData.map(d => d.contributions || 0),
        borderColor: 'rgb(59, 130, 246)', // Blue
        backgroundColor: 'transparent',
        fill: false,
        tension: 0.1, // Lower tension to show exponential curve better
        pointRadius: 0,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: 'rgb(59, 130, 246)',
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 2,
        borderWidth: 2,
        borderDash: [5, 5]
      },
      // Total Interest line (cumulative) - exponential growth
      {
        label: 'Total Interest',
        data: projectedData.map(d => d.interest || 0),
        borderColor: 'rgb(34, 197, 94)', // Green
        backgroundColor: 'transparent',
        fill: false,
        tension: 0, // No tension to show true exponential curve
        pointRadius: 0,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: 'rgb(34, 197, 94)',
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 2,
        borderWidth: 2
      },
      // Total (Contributions + Interest) line - shows exponential growth
      {
        label: 'Total (Contributions + Interest)',
        data: projectedData.map(d => d.total || d.amount),
        borderColor: 'rgb(99, 102, 241)', // Purple
        backgroundColor: 'rgba(99, 102, 241, 0.1)',
        fill: true,
        tension: 0, // No tension to show true exponential curve
        pointRadius: 0,
        pointHoverRadius: 6,
        pointHoverBackgroundColor: 'rgb(99, 102, 241)',
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 2,
        borderWidth: 3
      },
      // Subtle target goal reference line
      {
        label: 'Target Goal',
        data: projectedData.map(d => d.target),
        borderColor: 'rgba(34, 197, 94, 0.3)',
        backgroundColor: 'transparent',
        borderDash: [5, 5],
        borderWidth: 1,
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 0
      },
      // Milestone markers
      ...milestones.map((milestone, index) => {
        const milestoneIndex = projectedData.findIndex(p => p.period === milestone.period);
        return {
          label: `${milestone.label} Milestone`,
          data: projectedData.map((d, i) => {
            // Only show milestone at the specific point
            if (i === milestoneIndex && milestoneIndex !== -1) {
              return milestone.amount;
            }
            return null;
          }),
          borderColor: 'rgb(251, 191, 36)',
          backgroundColor: 'rgb(251, 191, 36)',
          pointRadius: 6,
          pointHoverRadius: 8,
          showLine: false, // Don't draw lines, just points
          pointStyle: 'circle',
          pointHoverBackgroundColor: 'rgb(251, 191, 36)',
          pointHoverBorderColor: '#fff',
          pointHoverBorderWidth: 2
        };
      })
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top',
        display: true,
        labels: {
          filter: (item) => {
            // Show Total Contributions, Total Interest, and Total lines, hide Target Goal and milestones
            return item.text === 'Total Contributions' || item.text === 'Total Interest' || item.text === 'Total (Contributions + Interest)';
          },
          usePointStyle: true
        }
      },
      // Explicitly disable any data label plugins
      datalabels: {
        display: false // Completely disable all data labels on chart
      },
      // Also disable any annotation plugins that might show values
      annotation: {
        annotations: {}
      },
      title: {
        display: true,
        text: goalName,
        font: {
          size: 18,
          weight: 'bold'
        }
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        displayColors: true,
        filter: function(tooltipItem) {
          // Show tooltip for Contributions, Interest, and Total (indices 0, 1, 2)
          // Hide Target Goal (index 3) and milestones
          return tooltipItem.datasetIndex < 3;
        },
        callbacks: {
          title: function(context) {
            return context[0].label;
          },
          label: function(context) {
            const value = context.parsed.y;
            if (value === null || value === undefined) return null;
            const label = context.dataset.label || '';
            return `${label}: $${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          },
          afterBody: function(context) {
            const dataIndex = context[0].dataIndex;
            const dataPoint = projectedData[dataIndex];
            if (!dataPoint) return [];
            
            const percentage = ((dataPoint.total || dataPoint.amount) / targetAmount) * 100;
            return [`Progress: ${percentage.toFixed(1)}%`];
          }
        },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: Math.max(targetAmount * 1.1, ...projectedData.map(d => d.total || d.amount || 0)), // Ensure target is visible with 10% padding
        ticks: {
          callback: function(value) {
            return '$' + value.toLocaleString();
          },
          maxTicksLimit: 8
        },
        title: {
          display: true,
          text: 'Amount ($)'
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)'
        }
      },
      x: {
        title: {
          display: true,
          text: 'Time'
        },
        ticks: {
          maxTicksLimit: timePeriod === 'annual' ? 10 : timePeriod === 'quarterly' ? 20 : 12,
          autoSkip: true
        },
        grid: {
          display: false
        }
      }
    }
  };

  if (projectedData.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Adjust the metrics below to see your projection
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Time Period Selector */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Time Period</InputLabel>
          <Select
            value={timePeriod}
            label="Time Period"
            onChange={(e) => onTimePeriodChange(e.target.value)}
          >
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="quarterly">Quarterly</MenuItem>
            <MenuItem value="annual">Annual</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Chart */}
      <Box sx={{ height: 400, width: '100%' }}>
        <Line data={chartData} options={options} />
      </Box>

      {/* Milestone Legend */}
      {milestones.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          {milestones.map((milestone, index) => {
            const date = new Date();
            date.setMonth(date.getMonth() + milestone.month);
            return (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'rgb(251, 191, 36)' }} />
                <Typography variant="caption" color="text.secondary">
                  {milestone.label}: {date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default JourneyTimelineChart;

